import { Box, Typography } from "@mui/material";
import { Calendar } from "react-big-calendar";
import moment from "moment";
import { Appointment } from "../../../state/appointments";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";
import {
  calendarWrapperSx,
  fullWidthLayout,
  eventStyleGetter,
  calendarEvents,
  CalendarEventLabel
} from "./calendarShared";

/** Slot the doctor is about to book, drawn dashed so it reads as not-yet-real. */
export interface PendingSlot {
  start: Date;
  end: Date;
  patientName: string;
}

interface Props {
  /** Day to show — follows the date picked in the schedule form. */
  date: moment.Moment | null;
  appointments: Appointment[];
  localizer: any;
  /** Fallback scroll position used while no date is picked. */
  defaultScrollTime: Date;
  /** Shown alongside the real appointments while confirming a booking. */
  pendingSlot?: PendingSlot;
}

/**
 * Read-only "Day" view of the doctor's calendar for the date currently selected
 * in the schedule form, so they can see that day's agenda while scheduling.
 */
const dayTitleSx = {
  ...typography.heading.s,
  color: semanticColors.text.primary,
  mb: 2,
  textTransform: "capitalize"
};

const PENDING_ID = "__pending__";

const pendingEventStyle = {
  style: {
    background: semanticColors.surface.canvas,
    border: `2px dashed ${semanticColors.action.primary}`,
    borderRadius: "4px",
    color: semanticColors.action.primary,
    fontFamily: "'Inter', sans-serif",
    fontSize: "12px",
    fontWeight: 600
  }
};

export default function DaySchedulePreview({
  date,
  appointments,
  localizer,
  defaultScrollTime,
  pendingSlot
}: Props) {
  const day = date ? date.toDate() : new Date();
  // Keep the preview scrolled to the time being scheduled, with the previous
  // hour still in view for context.
  const scrollTime = date
    ? date
        .clone()
        .subtract(1, "hour")
        .toDate()
    : defaultScrollTime;
  // react-big-calendar only honours `scrollToTime` on mount, so remount the
  // calendar whenever the target hour changes to make the preview follow the
  // time picked in the form.
  const scrollKey = moment(scrollTime).format("YYYY-MM-DD-HH");
  const events: any[] = calendarEvents(appointments);
  if (pendingSlot) events.push({ ...pendingSlot, id: PENDING_ID });

  return (
    <Box>
      <Typography sx={dayTitleSx}>
        {moment(day).format("dddd, DD/MM/YYYY")}
      </Typography>
      <Box translate="no" sx={calendarWrapperSx}>
        <Calendar
          key={scrollKey}
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          date={day}
          view="day"
          views={["day"]}
          toolbar={false}
          onNavigate={() => {}}
          onView={() => {}}
          step={15}
          timeslots={2}
          dayLayoutAlgorithm={fullWidthLayout}
          scrollToTime={scrollTime}
          eventPropGetter={(event: any) =>
            event.id === PENDING_ID
              ? pendingEventStyle
              : eventStyleGetter(event)
          }
          components={{ event: CalendarEventLabel }}
          style={{ height: 600 }}
        />
      </Box>
    </Box>
  );
}
